import React, { useState, useEffect } from 'react'
import { profileService } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import { formatRupees } from '../utils/currency'
import { 
  User, 
  Edit3, 
  Save, 
  X, 
  Camera, 
  Mail, 
  IndianRupee,
  Calendar,
  BarChart3,
  Target,
  TrendingUp,
  TrendingDown,
  Users
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'

const UserProfilePage = () => {
  const { user } = useAuth()
  const [profile, setProfile] = useState(null)
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [error, setError] = useState('')
  
  const [formData, setFormData] = useState({
    full_name: '',
    bio: '',
    profile_pic_url: '',
    monthly_budget: '',
  })

  useEffect(() => {
    if (user) {
      loadProfile()
      loadStats()
    }
  }, [user])

  const loadProfile = async () => {
    try {
      setLoading(true)
      const data = await profileService.getProfile()
      setProfile(data)
      setFormData({
        full_name: data.full_name || '',
        bio: data.bio || '',
        profile_pic_url: data.profile_pic_url || '',
        monthly_budget: data.monthly_budget || '',
      })
    } catch (error) {
      console.error('Error loading profile:', error)
      setError('Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const data = await profileService.getProfileStats()
      setStats(data)
    } catch (error) {
      console.error('Error loading stats:', error)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleImageUpload = (e) => {
    // Auto-save is disabled - user will save manually
    // This prevents multiple API calls and gives user control
  }

  const handleSave = async () => {
    try {
      setError('')
      
      // Validate monthly budget if provided
      if (formData.monthly_budget && isNaN(parseFloat(formData.monthly_budget))) {
        setError('Monthly budget must be a valid number')
        return
      }

      setUpdateLoading(true)
      
      const updateData = {
        full_name: formData.full_name,
        bio: formData.bio,
        profile_pic_url: formData.profile_pic_url,
        monthly_budget: formData.monthly_budget ? parseFloat(formData.monthly_budget) : null,
      }
      
      const updated = await profileService.updateProfile(updateData)
      setProfile(updated)
      setEditing(false)
      
      // Reload stats to reflect budget changes
      loadStats()
    } catch (error) {
      console.error('Error updating profile:', error)
      setError(error.message || 'Failed to update profile')
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleCancel = () => {
    setError('')
    setEditing(false)
    // Reset form data to current profile values
    setFormData({
      full_name: profile?.full_name || '',
      bio: profile?.bio || '',
      profile_pic_url: profile?.profile_pic_url || '',
      monthly_budget: profile?.monthly_budget || '',
    })
  }

  if (loading) {
    return <LoadingSpinner message="Loading profile..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            User Profile
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your personal information and preferences
          </p>
        </div>
        
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Edit3 className="h-4 w-4" />
            <span>Edit Profile</span>
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={handleCancel}
              className="btn-secondary flex items-center space-x-2"
            >
              <X className="h-4 w-4" />
              <span>Cancel</span>
            </button>
            <button
              onClick={handleSave}
              disabled={updateLoading}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info Card */}
          <div className="card">
            <div className="flex items-center space-x-4 mb-6">
              <div className="relative">
                {(editing ? formData.profile_pic_url : profile?.profile_pic_url) ? (
                  <img
                    src={editing ? formData.profile_pic_url : profile?.profile_pic_url}
                    alt="Profile"
                    className="w-20 h-20 rounded-full object-cover border-4 border-primary-100 dark:border-primary-800"
                    onError={(e) => {
                      console.log('Image failed to load:', editing ? formData.profile_pic_url : profile?.profile_pic_url)
                      e.target.style.display = 'none'
                      const fallback = e.target.parentNode.querySelector('.fallback-avatar')
                      if (fallback) fallback.style.display = 'flex'
                    }}
                  />
                ) : null}
                <div 
                  className={`fallback-avatar w-20 h-20 rounded-full bg-primary-600 flex items-center justify-center text-white text-2xl font-bold ${(editing ? formData.profile_pic_url : profile?.profile_pic_url) ? 'hidden' : 'flex'}`}
                >
                  {(editing ? formData.full_name : profile?.full_name) ? (editing ? formData.full_name : profile?.full_name).charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase()}
                </div>
                {editing && (
                  <div className="absolute -bottom-2 -right-2 bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-full shadow-lg transition-colors cursor-pointer">
                    <Camera className="h-4 w-4" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {profile?.full_name || 'User'}
                </h2>
                <div className="flex items-center text-gray-600 dark:text-gray-400 mt-1">
                  <Mail className="h-4 w-4 mr-2" />
                  <span>{user?.email}</span>
                </div>
                {profile?.created_at && (
                  <div className="flex items-center text-gray-600 dark:text-gray-400 mt-1">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>Joined {new Date(profile.created_at).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>

            {editing ? (
              <div className="space-y-6">
                {/* Edit Form Header */}
                <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-800 rounded-lg flex items-center justify-center">
                        <Edit3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Edit Your Profile
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Update your personal information and preferences
                      </p>
                    </div>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="space-y-6">
                    {/* Personal Information Section */}
                    <div>
                      <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                        <User className="h-4 w-4 mr-2 text-gray-600" />
                        Personal Information
                      </h4>
                      <div className="space-y-4">
                        {/* Full Name */}
                        <div className="space-y-2">
                          <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                            <User className="h-4 w-4 mr-2 text-blue-600" />
                            Full Name *
                          </label>
                          <input
                            type="text"
                            name="full_name"
                            value={formData.full_name}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                            placeholder="Enter your full name"
                            required
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                            <User className="h-3 w-3 mr-1" />
                            This is how others will see your name in the expense tracker.
                          </p>
                        </div>

                        {/* Bio */}
                        <div className="space-y-2">
                          <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                            <Edit3 className="h-4 w-4 mr-2 text-green-600" />
                            Bio
                          </label>
                          <textarea
                            name="bio"
                            value={formData.bio}
                            onChange={handleInputChange}
                            rows={4}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none transition-all duration-200 shadow-sm hover:shadow-md"
                            placeholder="Tell us about yourself..."
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                            <Edit3 className="h-3 w-3 mr-1" />
                            Share a brief description about yourself, your role, or interests.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Appearance & Budget Section */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                      <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                        <Camera className="h-4 w-4 mr-2 text-gray-600" />
                        Appearance & Budget
                      </h4>
                      <div className="space-y-4">
                        {/* Profile Picture URL */}
                        <div className="space-y-2">
                          <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                            <Camera className="h-4 w-4 mr-2 text-purple-600" />
                            Profile Picture
                          </label>
                          <div className="space-y-3">
                            {/* Profile Picture Preview */}
                            {formData.profile_pic_url && (
                              <div className="flex items-center space-x-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div className="relative">
                                  <img
                                    src={formData.profile_pic_url}
                                    alt="Profile Preview"
                                    className="w-16 h-16 rounded-full object-cover border-2 border-purple-200 dark:border-purple-700"
                                    onError={(e) => {
                                      console.log('Image failed to load:', formData.profile_pic_url)
                                      e.target.style.display = 'none'
                                      const fallback = e.target.parentNode.querySelector('.preview-fallback')
                                      if (fallback) {
                                        fallback.style.display = 'flex'
                                        const status = e.target.parentNode.parentNode.querySelector('.status-text')
                                        if (status) {
                                          status.className = 'text-xs text-red-600 dark:text-red-400'
                                          status.textContent = '✗ Failed to load image - check URL'
                                        }
                                      }
                                    }}
                                    onLoad={(e) => {
                                      const status = e.target.parentNode.parentNode.querySelector('.status-text')
                                      if (status) {
                                        status.className = 'text-xs text-green-600 dark:text-green-400'
                                        status.textContent = '✓ Image loaded successfully'
                                      }
                                    }}
                                  />
                                  <div className="preview-fallback w-16 h-16 rounded-full bg-red-100 dark:bg-red-900 border-2 border-red-200 dark:border-red-700 items-center justify-center text-red-600 dark:text-red-400 text-xs font-bold hidden">
                                    ✗
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">Preview</p>
                                  <p className="status-text text-xs text-gray-600 dark:text-gray-400">
                                    Loading image...
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            <input
                              type="url"
                              name="profile_pic_url"
                              value={formData.profile_pic_url}
                              onChange={handleInputChange}
                              onBlur={handleImageUpload}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                              placeholder="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face"
                            />
                            
                            {/* Helper text with working examples */}
                            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                              <p className="font-medium">✓ Working examples you can try:</p>
                              <div className="space-y-1 pl-2">
                                <p className="break-all">• <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face</code></p>
                                <p className="break-all">• <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face</code></p>
                                <p className="break-all">• <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">https://images.unsplash.com/photo-1494790108755-2616b332c859?w=400&h=400&fit=crop&crop=face</code></p>
                              </div>
                              <p className="text-red-600 dark:text-red-400 mt-2">✗ Don't use: Unsplash gallery pages, iStock/Getty pages, or social media URLs</p>
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  const avatarUrl = `https://api.dicebear.com/7.x/initials/svg?seed=${formData.full_name || user?.email}`
                                  setFormData(prev => ({ ...prev, profile_pic_url: avatarUrl }))
                                }}
                                className="inline-flex items-center px-3 py-2 text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900 border border-purple-300 dark:border-purple-700 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors duration-200"
                              >
                                <User className="h-3 w-3 mr-1" />
                                Generate Avatar
                              </button>
                              
                              {/* Quick example buttons */}
                              <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, profile_pic_url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face' }))}
                                className="inline-flex items-center px-3 py-2 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900 border border-blue-300 dark:border-blue-700 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors duration-200"
                              >
                                <Camera className="h-3 w-3 mr-1" />
                                Male Example
                              </button>
                              
                              <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, profile_pic_url: 'https://images.unsplash.com/photo-1494790108755-2616b332c859?w=400&h=400&fit=crop&crop=face' }))}
                                className="inline-flex items-center px-3 py-2 text-xs font-medium text-pink-700 dark:text-pink-300 bg-pink-100 dark:bg-pink-900 border border-pink-300 dark:border-pink-700 rounded-lg hover:bg-pink-200 dark:hover:bg-pink-800 transition-colors duration-200"
                              >
                                <Camera className="h-3 w-3 mr-1" />
                                Female Example
                              </button>
                              
                              <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, profile_pic_url: '' }))}
                                className="inline-flex items-center px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                              >
                                <X className="h-3 w-3 mr-1" />
                                Remove
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Enter a direct URL to your profile picture (PNG, JPG, GIF). Use the example buttons above for working URLs, or upload your image to <a href="https://imgur.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Imgur</a> or <a href="https://postimages.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">PostImages</a> for free hosting.
                          </p>
                        </div>

                        {/* Monthly Budget */}
                        <div className="space-y-2">
                          <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                            <IndianRupee className="h-4 w-4 mr-2 text-yellow-600" />
                            Monthly Budget (₹)
                          </label>
                          <div className="relative">
                            <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="number"
                              name="monthly_budget"
                              value={formData.monthly_budget}
                              onChange={handleInputChange}
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                              placeholder="25000"
                              min="0"
                              step="100"
                            />
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                            <IndianRupee className="h-3 w-3 mr-1" />
                            Set your monthly spending budget to track your expenses effectively.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Bio Display */}
                {profile?.bio && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bio
                    </h3>
                    <p className="text-gray-900 dark:text-white">
                      {profile.bio}
                    </p>
                  </div>
                )}

                {/* Monthly Budget Display */}
                {profile?.monthly_budget > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Monthly Budget
                    </h3>
                    <div className="flex items-center text-primary-600 dark:text-primary-400">
                      <IndianRupee className="h-5 w-5 mr-2" />
                      <span className="text-xl font-semibold">
                        {formatRupees(profile.monthly_budget)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Statistics Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Quick Stats
            </h3>
            
            {stats ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <BarChart3 className="h-5 w-5 text-blue-600 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Total Expenses</span>
                  </div>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {formatRupees(stats.total_expenses)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Target className="h-5 w-5 text-green-600 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Total Budgets</span>
                  </div>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {formatRupees(stats.total_budgets)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {stats.savings >= 0 ? (
                      <TrendingUp className="h-5 w-5 text-green-600 mr-2" />
                    ) : (
                      <TrendingDown className="h-5 w-5 text-red-600 mr-2" />
                    )}
                    <span className="text-sm text-gray-600 dark:text-gray-400">Savings</span>
                  </div>
                  <span className={`font-semibold ${stats.savings >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatRupees(stats.savings)}
                  </span>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                        {stats.expense_count}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Expenses</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                        {stats.budget_count}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Budgets</p>
                    </div>
                  </div>
                </div>

                {/* Sharing Stats */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <Users className="h-4 w-4 text-purple-600 mr-2" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Budgets Shared</span>
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {stats.budgets_shared}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Users className="h-4 w-4 text-orange-600 mr-2" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Received</span>
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {stats.budgets_received}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            )}
          </div>

          {/* Budget Progress if monthly budget is set */}
          {profile?.monthly_budget > 0 && stats && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Monthly Budget Progress
              </h3>
              
              {(() => {
                const percentage = (stats.total_expenses / profile.monthly_budget) * 100
                const remaining = profile.monthly_budget - stats.total_expenses
                
                return (
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Spent this month</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {percentage.toFixed(1)}%
                      </span>
                    </div>
                    
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all duration-300 ${
                          percentage > 90 
                            ? 'bg-red-500' 
                            : percentage > 70 
                            ? 'bg-yellow-500' 
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(100, percentage)}%` }}
                      ></div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {formatRupees(stats.total_expenses)} / {formatRupees(profile.monthly_budget)}
                      </span>
                      <span className={`text-sm font-semibold ${remaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {remaining >= 0 ? formatRupees(remaining) + ' left' : 'Over budget by ' + formatRupees(-remaining)}
                      </span>
                    </div>
                  </div>
                )
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UserProfilePage
